<template>
  <div
    class="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 hover:border-teal-300 dark:hover:border-teal-700 transition-all duration-200 px-3 py-2.5 sm:px-4 sm:py-3"
    :class="{
      'opacity-60': isPerformed && !isUpNext,
      'ring-2 ring-teal-500 ring-offset-2 border-teal-500': hasVoted && !isPerformed && !isUpNext,
      'bg-teal-50/50 dark:bg-teal-900/10': hasVoted && !isPerformed && !isUpNext,
      'ring-2 ring-amber-500/80 ring-offset-0 border-amber-500/50 bg-amber-50/50 dark:bg-amber-900/10':
        isUpNext,
      'shadow-sm hover:shadow-md': !isPerformed,
      'cursor-pointer': clickable
    }"
  >
    <div class="songcard flex items-center justify-between gap-3">
      <!-- Left: title / author / genre / state badges -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-1.5">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {{ song.title }}
          </h3>
          <i
            v-if="isPerformed"
            class="pi pi-check-circle text-teal-600 dark:text-teal-400 text-xs"
            :title="
              hasVoted && isPerformed
                ? t('event.votedAndPerformedTooltip')
                : t('event.performedTooltip')
            "
          ></i>
        </div>
        <p v-if="song.author" class="text-gray-600 dark:text-gray-400 text-xs truncate">
          {{ song.author }}
        </p>
        <div class="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <span
            v-for="category in categoryList"
            :key="category"
            class="inline-block px-1.5 py-0.5 text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
          >
            {{ category }}
          </span>
          <span
            v-if="isPerformed && hasVoted"
            class="inline-block px-1.5 py-0.5 text-[11px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded font-semibold"
          >
            {{ t("event.performedAndVoted") }}
          </span>
          <span
            v-else-if="isPerformed"
            class="inline-block px-1.5 py-0.5 text-[11px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded font-semibold"
          >
            {{ t("event.performed") }}
          </span>
        </div>
      </div>

      <SongVoteControls
        :vote-count="voteCount"
        :is-performed="isPerformed"
        :is-up-next="isUpNext"
        :can-vote="canVote"
        :has-voted="hasVoted"
        :is-voting="isVoting"
        :is-vote-locked="isVoteLocked"
        :show-vote-count="showVoteCount"
        :show-vote-button="showVoteButton"
        :show-karaoke-indicator="showKaraokeIndicator"
        :is-karaoke-queued="isKaraokeQueued"
        @vote="handleVote"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import SongVoteControls from "./SongVoteControls.vue";

const { t } = useI18n();

const props = defineProps({
  song: {
    type: Object,
    required: true
  },
  eventId: {
    type: Number,
    required: true
  },
  voteCount: {
    type: Number,
    default: 0
  },
  isPerformed: {
    type: Boolean,
    default: false
  },
  isUpNext: {
    type: Boolean,
    default: false
  },
  canVote: {
    type: Boolean,
    default: true
  },
  hasVoted: {
    type: Boolean,
    default: false
  },
  isVoting: {
    type: Boolean,
    default: false
  },
  isVoteLocked: {
    type: Boolean,
    default: false
  },
  showVoteCount: {
    type: Boolean,
    default: true
  },
  showVoteButton: {
    type: Boolean,
    default: true
  },
  showKaraokeIndicator: {
    type: Boolean,
    default: false
  },
  isKaraokeQueued: {
    type: Boolean,
    default: false
  },
  clickable: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["vote"]);

const categoryList = computed(() => {
  if (!props.song) return [];
  if (Array.isArray(props.song.categories) && props.song.categories.length > 0) {
    return props.song.categories;
  }
  if (props.song.category) {
    return [props.song.category];
  }
  return [];
});

const handleVote = () => {
  emit("vote", props.song.id);
};
</script>
